<template>
  <div class="py-6">
    <div class="text-base-bold">Continue with Google Pay</div>
    <div class="text-base-regular mt-2">
      Google Pay is the fast, simple way to pay using the cards saved to your Google Account.
    </div>

    <google-pay-button
      v-if="!isSSR"
      class="mt-6"
      :environment="environment || 'TEST'"
      :buttonType.prop="'pay'"
      :buttonColor.prop="'black'"
      @loadpaymentdata="onLoadPaymentData"
      @error="onError"
    ></google-pay-button>
  </div>
</template>

<script setup lang="ts">
const isSSR = typeof window == 'undefined';
!isSSR && import('../../../node_modules/@google-pay/button-element/dist');
import { onMounted, watch, computed } from 'vue';
import { usePaymentStore } from '@/store';

const props = defineProps<{
  environment?: string;
  gatewayMerchantId?: string;
  merchantName?: string;
  merchantId?: string;
}>();

const paymentStore = usePaymentStore();

const paymentRequest = computed(() => {
  const amt = Number(paymentStore.amount || '0');

  return {
    apiVersion: 2,
    apiVersionMinor: 0,
    allowedPaymentMethods: [
      {
        type: 'CARD',
        parameters: {
          allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
          allowedCardNetworks: ['AMEX', 'VISA', 'MASTERCARD'],
        },
        tokenizationSpecification: {
          type: 'PAYMENT_GATEWAY',
          parameters: {
            gateway: 'fatzebra',
            gatewayMerchantId: props.gatewayMerchantId,
          },
        },
      },
    ],
    merchantInfo: {
      merchantId: props.merchantId,
      merchantName: props.merchantName || 'RACQ',
    },
    transactionInfo: {
      totalPriceStatus: 'FINAL',
      totalPriceLabel: 'Total',
      totalPrice: amt.toFixed(2),
      currencyCode: 'AUD',
      countryCode: 'AU',
    },
  };
});

onMounted(() => {
  paymentStore.googlePay = true;
  const button = document.querySelector('google-pay-button') as any;
  if (button) {
    button.paymentRequest = paymentRequest.value;
  }
});

// Keep the underlying web component's paymentRequest in sync with reactive updates
watch(paymentRequest, (val) => {
  const button = document.querySelector('google-pay-button') as any;
  if (button) {
    button.paymentRequest = val;
  }
});

const onLoadPaymentData = (event: CustomEvent<any>) => {
  try {
    // Google Pay token - this is the raw ECv2 JSON string we must forward to Online Services
    const token: string | undefined =
      event?.detail?.paymentMethodData?.tokenizationData?.token ||
      event?.detail?.paymentMethodData?.tokenizationData?.tokenizationData?.token;

    if (!token) {
      console.error('Google Pay token missing from payment data');
      return;
    }

    // Store token and trigger the shared submit flow via submitPaymentCallback
    paymentStore.setGooglePayToken(token);
  } catch (err) {
    console.error('Failed to process Google Pay payment data', err);
  }
};

const onError = (error: any) => {
  console.error('Google Pay error', {
    statusCode: error?.statusCode,
    statusMessage: error?.statusMessage,
  });
};
</script>
